import { useNavigate } from "@solidjs/router";
import {
  CreateInvitationsRequestConfig,
  DeleteInvitationRequestConfig,
  UpdateInvitationRequestConfig,
  useStorytellClient,
} from "@storytell/client";
import type { BoundaryId, Invitation, ProjectId, RoleName } from "@storytell/types";
import { StButton } from "@storytell/ui/buttons";
import {
  TbAlertTriangle,
  TbCheck,
  TbCopy,
  TbHelpCircle,
  TbLink,
  TbMail,
  TbPlus,
  TbTrash,
  TbUsers,
  TbWorld,
} from "solid-icons/tb";
import { type Accessor, type Component, For, Show, Suspense, createSignal } from "solid-js";
import { twMerge } from "tailwind-merge";
import { isFreeEmailDomain } from "~/utils/free-email-domains";
import { useWire } from "~/wire";
import { CreateDomainInviteForm } from "./CreateDomainInviteForm";
import { CreateEmailInviteForm } from "./CreateEmailInviteForm";
import { CreateLinkInviteForm } from "./CreateLinkInviteForm";
import { DeleteDomainModal } from "./DeleteDomainModal";
import { DeleteEmailModal } from "./DeleteEmailModal";
import { DeleteInviteModal } from "./DeleteInviteModal";
import { DomainInviteCard } from "./DomainInviteCard";
import { EmailInviteCard } from "./EmailInviteCard";
import { LinkInviteCard } from "./LinkInviteCard";
import { mapRoleToBackend, mapRoleToUI } from "./roleUtils";
import type { DomainInvite, EmailInvite, Role } from "./types";

interface ProjectShareSectionProps {
  projectId: string;
  invitations: Accessor<Invitation[]>;
  refetchInvitations: () => void;
  mutateInvitations: (fn: (invites: Invitation[]) => Invitation[]) => void;

  embedded?: boolean;
}

export const ProjectShareSection: Component<ProjectShareSectionProps> = (props) => {
  const wire = useWire();
  const client = useStorytellClient();
  const navigate = useNavigate();

  // Get current user's role in the project
  const currentUserRole = (): Role | null => {
    const project = wire.projects.get(props.projectId as ProjectId);
    if (!project?.role) return null;
    return mapRoleToUI(project.role);
  };

  // Get roles available for the current user
  // In modal: show all three roles for owners, only user's role for collaborators/readers
  // On project management page: show all roles for owners, only user's role for others
  const availableRoles = (): Role[] => {
    const userRole = currentUserRole();
    const isEmbedded = props.embedded === true;

    // In modal (embedded)
    if (isEmbedded) {
      // Owners see all three roles in the modal
      if (userRole === "Owner") {
        return ["Owner", "Collaborator", "Reader"];
      }
      // Collaborators and readers only see their own role
      // If we can't determine the role, default to Collaborator to be safe
      return userRole ? [userRole] : ["Collaborator" as Role];
    }

    // On project management page
    if (!userRole) {
      return ["Owner", "Collaborator", "Reader"];
    }
    // Show all roles for owners on project management page
    if (userRole === "Owner") {
      return ["Owner", "Collaborator", "Reader"];
    }
    return [userRole];
  };

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = createSignal(false);
  const [deleteTarget, setDeleteTarget] = createSignal<{ inviteId: string; role: RoleName } | null>(null);

  // Domain delete modal state
  const [domainDeleteModalOpen, setDomainDeleteModalOpen] = createSignal(false);
  const [domainDeleteTarget, setDomainDeleteTarget] = createSignal<{
    inviteId: string;
    domain: string;
    role: RoleName;
  } | null>(null);

  // Email delete modal state
  const [emailDeleteModalOpen, setEmailDeleteModalOpen] = createSignal(false);
  const [emailDeleteTarget, setEmailDeleteTarget] = createSignal<{
    inviteId: string;
    email: string;
    role: RoleName;
  } | null>(null);

  // Copied state for invite links
  const [copiedInviteId, setCopiedInviteId] = createSignal<string | null>(null);
  // Track Owner invite links that have been copied (warning persists)
  const [copiedOwnerInviteIds, setCopiedOwnerInviteIds] = createSignal<Set<string>>(new Set());

  // Create invitation state
  const [isCreatingInvite, setIsCreatingInvite] = createSignal(false);
  const [showCreateLinkInvite, setShowCreateLinkInvite] = createSignal(false);
  const [showCreateDomainInvite, setShowCreateDomainInvite] = createSignal(false);
  const [showCreateEmailInvite, setShowCreateEmailInvite] = createSignal(false);

  // Create link invite state - initialize with user's role
  const [newLinkInviteRole, setNewLinkInviteRole] = createSignal<Role>(currentUserRole() || "Collaborator");

  // Create domain invite state - initialize with user's role
  const [newDomainInvites, setNewDomainInvites] = createSignal<DomainInvite[]>([]);
  const [pendingDomains, setPendingDomains] = createSignal<string[]>([]);
  const [currentDomainInput, setCurrentDomainInput] = createSignal("");
  const [newDomainRole, setNewDomainRole] = createSignal<Role>(currentUserRole() || "Collaborator");
  const [domainError, setDomainError] = createSignal<string>("");

  // Create email invite state - initialize with user's role
  const [newEmailInvites, setNewEmailInvites] = createSignal<EmailInvite[]>([]);
  const [pendingEmails, setPendingEmails] = createSignal<string[]>([]);
  const [currentEmailInput, setCurrentEmailInput] = createSignal("");
  const [newEmailRole, setNewEmailRole] = createSignal<Role>(currentUserRole() || "Collaborator");
  const [emailError, setEmailError] = createSignal<string>("");

  // Helper functions to categorize invitations
  const linkOnlyInvites = () => props.invitations();

  // Get invite for a specific role - returns any invitation with that role that has a link
  const getInviteForRole = (role: Role): Invitation | undefined => {
    const backendRole = mapRoleToBackend(role);
    const allInvites = props.invitations() || [];
    const matchingInvite = allInvites.find((inv) => {
      // Check if role matches
      if (inv.role !== backendRole) return false;

      // Check if link exists and has a valid URL or destination
      const linkUrl = inv.link?.url;
      const linkDestination = inv.link?.destination;
      const hasValidLink = (linkUrl && linkUrl.trim() !== "") || (linkDestination && linkDestination.trim() !== "");

      return hasValidLink;
    });

    return matchingInvite;
  };

  const domainInvitesData = () => {
    const invites = props.invitations() || [];
    return invites.flatMap((inv) =>
      (inv.domains || []).map((domain) => ({
        domain: domain.name,
        domainId: domain.id,
        role: inv.role,
        inviteLink: inv.link.url || inv.link.destination || "",
        inviteId: inv.id,
        fullInvite: inv,
      })),
    );
  };

  const emailInvitesData = () => {
    const invites = props.invitations() || [];
    return invites.flatMap((inv) =>
      (inv.humans || []).map((human) => ({
        email: human.identifier.id,
        humanId: human.id,
        role: inv.role,
        inviteLink: inv.link.url || inv.link.destination || "",
        inviteId: inv.id,
        fullInvite: inv,
      })),
    );
  };

  const copyInviteLink = async (invite: Invitation) => {
    try {
      const linkUrl = invite.link.url || invite.link.destination;
      if (linkUrl) {
        await navigator.clipboard.writeText(linkUrl);
        setCopiedInviteId(invite.id);
        setTimeout(() => setCopiedInviteId(null), 2000);

        // Track Owner invites that have been copied (warning persists)
        const backendRole = mapRoleToBackend("Owner");
        if (invite.role === backendRole) {
          setCopiedOwnerInviteIds((prev) => new Set(prev).add(invite.id));
        }
      }
    } catch (err) {
      console.error("Failed to copy invite link", err);
    }
  };

  const copyLink = async (link: string, id: string) => {
    try {
      if (link) {
        await navigator.clipboard.writeText(link);
        setCopiedInviteId(id);
        setTimeout(() => setCopiedInviteId(null), 2000);
      }
    } catch (err) {
      console.error("Failed to copy invite link", err);
    }
  };

  const handleDeleteLinkInvite = (inviteId: string, role: RoleName) => {
    setDeleteTarget({ inviteId, role });
    setDeleteModalOpen(true);
  };

  const confirmDeleteLinkInvite = async () => {
    const target = deleteTarget();
    if (!target) return;

    try {
      const request = DeleteInvitationRequestConfig.withBearerToken(wire.auth.token());
      const response = await client.fetch(request, { invitationId: target.inviteId as BoundaryId }, undefined);

      if (response.isErr()) {
        console.error("Failed to delete invitation", response.error);
        alert("Failed to delete invitation. Please try again.");
        return;
      }

      props.mutateInvitations((invitations) => invitations.filter((inv) => inv.id !== target.inviteId));
      props.refetchInvitations();
    } catch (error) {
      console.error("Error deleting invitation", error);
      alert("An unexpected error occurred while deleting the invitation.");
    } finally {
      setDeleteModalOpen(false);
      setDeleteTarget(null);
    }
  };

  const handleDeleteDomainFromInvite = (inviteId: string, domain: string, role: RoleName) => {
    setDomainDeleteTarget({ inviteId, domain, role });
    setDomainDeleteModalOpen(true);
  };

  const confirmDeleteDomainFromInvite = async () => {
    const target = domainDeleteTarget();
    if (!target) return;

    try {
      const currentInvite = props.invitations()?.find((inv) => inv.id === target.inviteId);
      if (!currentInvite) {
        alert("Invitation not found.");
        return;
      }

      const updatedDomains =
        currentInvite.domains?.filter((d) => d.name !== target.domain)?.map((d) => ({ name: d.name })) || [];
      const currentUsers = currentInvite.humans?.map((h) => ({ email: h.identifier.id })) || [];

      const updateRequest = UpdateInvitationRequestConfig.withBearerToken(wire.auth.token());
      const updateResponse = await client.fetch(
        updateRequest,
        { invitationId: target.inviteId as BoundaryId },
        {
          domains: updatedDomains.length > 0 ? updatedDomains : undefined,
          users: currentUsers.length > 0 ? currentUsers : undefined,
          role: currentInvite.role,
        },
      );

      if (updateResponse.isErr()) {
        console.error("Failed to update invitation", updateResponse.error);
        alert("Failed to remove domain from invitation. Please try again.");
        return;
      }

      props.mutateInvitations((invitations) =>
        invitations.map((inv) => (inv.id === target.inviteId ? updateResponse.value : inv)),
      );

      props.refetchInvitations();
    } catch (error) {
      console.error("Error updating invitation", error);
      alert("An unexpected error occurred while removing the domain.");
    } finally {
      setDomainDeleteModalOpen(false);
      setDomainDeleteTarget(null);
    }
  };

  const handleDeleteEmailFromInvite = async (inviteId: string, email: string, role: RoleName) => {
    setEmailDeleteTarget({ inviteId, email, role });
    setEmailDeleteModalOpen(true);
  };

  const confirmDeleteEmailFromInvite = async () => {
    const target = emailDeleteTarget();
    if (!target) return;

    try {
      const currentInvite = props.invitations()?.find((inv) => inv.id === target.inviteId);
      if (!currentInvite) {
        alert("Invitation not found.");
        setEmailDeleteModalOpen(false);
        setEmailDeleteTarget(null);
        return;
      }

      const updatedUsers =
        currentInvite.humans
          ?.filter((h) => h.identifier.id !== target.email)
          ?.map((h) => ({ email: h.identifier.id })) || [];
      const currentDomains = currentInvite.domains?.map((d) => ({ name: d.name })) || [];

      const updateRequest = UpdateInvitationRequestConfig.withBearerToken(wire.auth.token());
      const updateResponse = await client.fetch(
        updateRequest,
        { invitationId: target.inviteId as BoundaryId },
        {
          domains: currentDomains.length > 0 ? currentDomains : undefined,
          users: updatedUsers.length > 0 ? updatedUsers : undefined,
          role: currentInvite.role,
        },
      );

      if (updateResponse.isErr()) {
        console.error("Failed to update invitation", updateResponse.error);
        alert("Failed to remove email from invitation. Please try again.");
        setEmailDeleteModalOpen(false);
        setEmailDeleteTarget(null);
        return;
      }

      props.mutateInvitations((invitations) =>
        invitations.map((inv) => (inv.id === target.inviteId ? updateResponse.value : inv)),
      );

      setEmailDeleteModalOpen(false);
      setEmailDeleteTarget(null);
    } catch (error) {
      console.error("Error removing email from invitation", error);
      alert("An unexpected error occurred while removing the email.");
      setEmailDeleteModalOpen(false);
      setEmailDeleteTarget(null);
    }
  };

  const getAvailableLinkInviteRoles = (): Role[] => {
    const existingRoles = linkOnlyInvites().map((inv) => mapRoleToUI(inv.role));
    const allRoles: Role[] = ["Owner", "Collaborator", "Reader"];
    return allRoles.filter((role) => !existingRoles.includes(role));
  };

  const createLinkInvitation = async () => {
    setIsCreatingInvite(true);

    try {
      const request = CreateInvitationsRequestConfig.withBearerToken(wire.auth.token());
      const response = await client.fetch(request, undefined, [
        {
          role: mapRoleToBackend(newLinkInviteRole()),
          // For link-only invitations, omit domains and users (don't send empty arrays)
        },
      ]);

      if (response.isErr()) {
        console.error("Failed to create link invitation", response.error);
        alert("Failed to create invitation link. Please try again.");
        return;
      }

      props.mutateInvitations((i) => [...i, ...response.value]);
      props.refetchInvitations();
    } catch (error) {
      console.error("Error creating link invitation", error);
      alert("An unexpected error occurred while creating the invitation.");
    } finally {
      setIsCreatingInvite(false);
      setShowCreateLinkInvite(false);
    }
  };

  const createLinkInvitationForRole = async (role: Role) => {
    setIsCreatingInvite(true);

    try {
      // Check if invite already exists for this role
      const existingInvite = getInviteForRole(role);
      if (existingInvite) {
        copyInviteLink(existingInvite);
        setIsCreatingInvite(false);
        return;
      }

      const request = CreateInvitationsRequestConfig.withBearerToken(wire.auth.token());
      const response = await client.fetch(request, undefined, [
        {
          role: mapRoleToBackend(role),
          // For link-only invitations, omit domains and users (don't send empty arrays)
        },
      ]);

      if (response.isErr()) {
        console.error("Failed to create link invitation", response.error);
        const errorObj = response.error as any;
        const errorMessage = errorObj?.message || errorObj?.errorString || "Unknown error";
        console.error("Error details:", {
          error: response.error,
          errorMessage,
          errorCode: errorObj?.code,
          role,
        });

        // Check if it's a duplicate error
        if (errorMessage.toLowerCase().includes("duplicate") || errorMessage.toLowerCase().includes("already exists")) {
          // Refetch and try to copy if it exists now
          props.refetchInvitations();
          setTimeout(() => {
            const updatedInvite = getInviteForRole(role);
            if (updatedInvite) {
              copyInviteLink(updatedInvite);
            } else {
              alert(`Failed to create invitation link: ${errorMessage}. Please try again.`);
            }
          }, 500);
        } else {
          alert(`Failed to create invitation link: ${errorMessage}. Please try again.`);
        }
        return;
      }

      props.mutateInvitations((i) => [...i, ...response.value]);

      // Try to copy the link from the response first, otherwise refetch
      const createdInvite = response.value.find((inv) => inv.role === mapRoleToBackend(role));
      if (createdInvite && (createdInvite.link?.url || createdInvite.link?.destination)) {
        // Copy immediately from the response
        copyInviteLink(createdInvite);
      } else {
        // If link not in response, refetch and then copy
        props.refetchInvitations();
        setTimeout(() => {
          const newInvite = getInviteForRole(role);
          if (newInvite) {
            copyInviteLink(newInvite);
          }
        }, 300);
      }
    } catch (error) {
      console.error("Error creating link invitation", error);
      alert("An unexpected error occurred while creating the invitation.");
    } finally {
      setIsCreatingInvite(false);
    }
  };

  const addNewDomainInvites = () => {
    const domains = pendingDomains();
    if (domains.length > 0) {
      const existingDomains = new Set([
        ...domainInvitesData().map((item) => item.domain.toLowerCase()),
        ...newDomainInvites().map((item) => item.domain.toLowerCase()),
      ]);

      const duplicateDomains = domains.filter((domain) => existingDomains.has(domain.toLowerCase()));
      const freeDomains = domains.filter((domain) => isFreeEmailDomain(domain));
      const validDomains = domains.filter(
        (domain) => !existingDomains.has(domain.toLowerCase()) && !isFreeEmailDomain(domain),
      );

      if (duplicateDomains.length > 0 && freeDomains.length > 0) {
        const dupList = duplicateDomains.join(", ");
        const freeList = freeDomains.join(", ");
        setDomainError(
          `Domain(s) ${dupList} already exist. Free email domains like ${freeList} cannot be added. Invite individual users via email instead.`,
        );
      } else if (duplicateDomains.length > 0) {
        const domain = duplicateDomains.join(", ");
        setDomainError(`Domain ${domain} already has access.`);
      } else if (freeDomains.length > 0) {
        const domain = freeDomains.join(", ");
        setDomainError(`Free email domains like ${domain} cannot be added. Invite individual users via email instead.`);
      } else {
        setDomainError("");
      }

      if (validDomains.length > 0) {
        const newInvites = validDomains.map((domain) => ({
          id: crypto.randomUUID(),
          domain,
          role: newDomainRole(),
        }));
        setNewDomainInvites([...newDomainInvites(), ...newInvites]);
      }

      setPendingDomains([]);
    }
  };

  const removeNewDomainInvite = (id: string) => {
    setNewDomainInvites(newDomainInvites().filter((invite) => invite.id !== id));
  };

  const updateNewDomainRole = (id: string, role: Role) => {
    setNewDomainInvites(newDomainInvites().map((invite) => (invite.id === id ? { ...invite, role } : invite)));
  };

  const createDomainInvitations = async () => {
    const domains = newDomainInvites();
    if (domains.length === 0) return;

    setIsCreatingInvite(true);
    try {
      const domainsByRole = new Map<Role, string[]>();
      for (const domainInvite of domains) {
        const existing = domainsByRole.get(domainInvite.role) || [];
        existing.push(domainInvite.domain);
        domainsByRole.set(domainInvite.role, existing);
      }

      for (const [role, domainNames] of domainsByRole.entries()) {
        const backendRole = mapRoleToBackend(role);
        const existingInvite = props.invitations()?.find((inv) => inv.role === backendRole);

        if (existingInvite) {
          const currentDomains = existingInvite.domains?.map((d) => ({ name: d.name })) || [];
          const currentUsers = existingInvite.humans?.map((h) => ({ email: h.identifier.id })) || [];
          const newDomains = domainNames.map((name) => ({ name }));
          const allDomains = [...currentDomains, ...newDomains];
          const uniqueDomains = Array.from(new Set(allDomains.map((d) => d.name))).map((name) => ({ name }));

          const updateRequest = UpdateInvitationRequestConfig.withBearerToken(wire.auth.token());
          const updateResponse = await client.fetch(
            updateRequest,
            { invitationId: existingInvite.id as BoundaryId },
            {
              domains: uniqueDomains,
              users: currentUsers,
              role: backendRole,
            },
          );

          if (updateResponse.isErr()) {
            console.error("Failed to update invitation", updateResponse.error);
            alert(`Failed to add domains to ${role} invitation. Please try again.`);
            return;
          }

          props.mutateInvitations((invitations) =>
            invitations.map((inv) => (inv.id === existingInvite.id ? updateResponse.value : inv)),
          );
        } else {
          const createRequest = CreateInvitationsRequestConfig.withBearerToken(wire.auth.token());
          const createResponse = await client.fetch(createRequest, undefined, [
            {
              role: backendRole,
              domains: domainNames.map((name) => ({ name })),
              users: [],
            },
          ]);

          if (createResponse.isErr()) {
            console.error("Failed to create domain invitations", createResponse.error);
            alert(`Failed to create ${role} domain invitation. Please try again.`);
            return;
          }

          props.mutateInvitations((invitations) => [...invitations, ...createResponse.value]);
        }
      }

      setNewDomainInvites([]);
      setPendingDomains([]);
      props.refetchInvitations();

      // Scroll to domain section after a brief delay to allow DOM to update
      setTimeout(() => {
        const domainSection = document.getElementById("domain-invites-section");
        if (domainSection) {
          domainSection.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    } catch (error) {
      console.error("Error creating domain invitations", error);
      alert("An unexpected error occurred while creating the invitations.");
    } finally {
      setIsCreatingInvite(false);
      closeCreateDomainInvite();
    }
  };

  const addNewEmailInvites = () => {
    const emails = pendingEmails();
    if (emails.length > 0) {
      const currentUserEmail = wire.auth.user()?.primaryEmail?.toLowerCase();
      const existingEmails = new Set(emailInvitesData().map((item) => item.email.toLowerCase()));
      const pendingEmailSet = new Set(newEmailInvites().map((item) => item.email.toLowerCase()));

      const selfInvites = emails.filter((email) => currentUserEmail && email.toLowerCase() === currentUserEmail);
      const duplicateEmails = emails.filter(
        (email) => existingEmails.has(email.toLowerCase()) || pendingEmailSet.has(email.toLowerCase()),
      );

      const validEmails = emails.filter(
        (email) =>
          !existingEmails.has(email.toLowerCase()) &&
          !pendingEmailSet.has(email.toLowerCase()) &&
          !(currentUserEmail && email.toLowerCase() === currentUserEmail),
      );

      if (selfInvites.length > 0 && duplicateEmails.length > 0) {
        const selfList = selfInvites.join(", ");
        const dupList = duplicateEmails.join(", ");
        setEmailError(`You cannot invite yourself (${selfList}). Email ${dupList} has already been invited.`);
      } else if (selfInvites.length > 0) {
        const emailList = selfInvites.join(", ");
        setEmailError(`You cannot invite yourself (${emailList}).`);
      } else if (duplicateEmails.length > 0) {
        const emailList = duplicateEmails.join(", ");
        setEmailError(`Email ${emailList} has already been invited.`);
      } else {
        setEmailError("");
      }

      if (validEmails.length > 0) {
        const newInvites = validEmails.map((email) => ({
          id: crypto.randomUUID(),
          email,
          role: newEmailRole(),
        }));
        setNewEmailInvites([...newEmailInvites(), ...newInvites]);
      }

      setPendingEmails([]);
    }
  };

  const removeNewEmailInvite = (id: string) => {
    setNewEmailInvites(newEmailInvites().filter((invite) => invite.id !== id));
  };

  const updateNewEmailRole = (id: string, role: Role) => {
    setNewEmailInvites(newEmailInvites().map((invite) => (invite.id === id ? { ...invite, role } : invite)));
  };

  const createEmailInvitations = async () => {
    const emails = newEmailInvites();
    if (emails.length === 0) return;

    setIsCreatingInvite(true);
    try {
      const emailsByRole = new Map<Role, string[]>();
      for (const emailInvite of emails) {
        const existing = emailsByRole.get(emailInvite.role) || [];
        existing.push(emailInvite.email);
        emailsByRole.set(emailInvite.role, existing);
      }

      for (const [role, emailAddresses] of emailsByRole.entries()) {
        const backendRole = mapRoleToBackend(role);
        const existingInvite = props.invitations()?.find((inv) => inv.role === backendRole);

        if (existingInvite) {
          const currentDomains = existingInvite.domains?.map((d) => ({ name: d.name })) || [];
          const currentUsers = existingInvite.humans?.map((h) => ({ email: h.identifier.id })) || [];
          const newUsers = emailAddresses.map((email) => ({ email }));
          const allUsers = [...currentUsers, ...newUsers];
          const uniqueUsers = Array.from(new Set(allUsers.map((u) => u.email))).map((email) => ({ email }));

          const updateRequest = UpdateInvitationRequestConfig.withBearerToken(wire.auth.token());
          const updateResponse = await client.fetch(
            updateRequest,
            { invitationId: existingInvite.id as BoundaryId },
            {
              domains: currentDomains,
              users: uniqueUsers,
              role: backendRole,
            },
          );

          if (updateResponse.isErr()) {
            console.error("Failed to update invitation", updateResponse.error);
            alert(`Failed to add emails to ${role} invitation. Please try again.`);
            return;
          }

          props.mutateInvitations((invitations) =>
            invitations.map((inv) => (inv.id === existingInvite.id ? updateResponse.value : inv)),
          );
        } else {
          const createRequest = CreateInvitationsRequestConfig.withBearerToken(wire.auth.token());
          const createResponse = await client.fetch(createRequest, undefined, [
            {
              role: backendRole,
              domains: [],
              users: emailAddresses.map((email) => ({ email })),
            },
          ]);

          if (createResponse.isErr()) {
            console.error("Failed to create email invitations", createResponse.error);
            alert(`Failed to create ${role} email invitation. Please try again.`);
            return;
          }

          props.mutateInvitations((invitations) => [...invitations, ...createResponse.value]);
        }
      }

      setNewEmailInvites([]);
      setPendingEmails([]);
      props.refetchInvitations();

      // Scroll to email section after a brief delay to allow DOM to update
      setTimeout(() => {
        const emailSection = document.getElementById("email-invites-section");
        if (emailSection) {
          emailSection.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    } catch (error) {
      console.error("Error creating email invitations", error);
      alert("An unexpected error occurred while creating the invitations.");
    } finally {
      setIsCreatingInvite(false);
      closeCreateEmailInvite();
    }
  };

  const closeCreateDomainInvite = () => {
    setShowCreateDomainInvite(false);
    setDomainError("");
  };

  const closeCreateEmailInvite = () => {
    setShowCreateEmailInvite(false);
    setEmailError("");
  };

  return (
    <>
      <div
        class={twMerge(
          "bg-canvas border border-primary-border rounded-xl p-6 w-full max-w-full overflow-x-hidden",
          props.embedded && "bg-transparent border-none p-0",
        )}
      >
        <Show when={!props.embedded}>
          <div id="share-section" class="flex items-center gap-2 mb-4">
            <TbUsers class="w-5 h-5 text-brand" />
            <h2 class="text-xl font-semibold">Share this Project</h2>
          </div>
        </Show>

        <Suspense fallback={<p class="text-sm text-muted-text">Loading invitations...</p>}>
          <Show
            when={props.invitations() && props.invitations()!.length > 0}
            fallback={
              <Show
                when={props.embedded}
                fallback={
                  <div class="space-y-6">
                    {/* Secret Link Section - Project Management Page (empty state) */}
                    <div class="space-y-3">
                      <div class="flex items-center justify-between">
                        <h3 class="text-sm font-semibold flex items-center gap-2">
                          <TbLink class="w-4 h-4" />
                          Share a Secret Link
                        </h3>
                        <a
                          href="https://go.storytell.ai/learn-project-roles"
                          target="_blank"
                          rel="noreferrer"
                          class="inline-flex items-center gap-1 text-xs text-brand underline hover:underline-offset-2"
                        >
                          <TbHelpCircle class="w-3.5 h-3.5" />
                          Learn about Project Roles
                        </a>
                      </div>
                      <p class="text-xs text-muted-text">Anyone with this link will be able to join the project.</p>
                      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 w-full min-w-0">
                        <For each={availableRoles()}>
                          {(role) => {
                            const invite = () => getInviteForRole(role);
                            const hasInvite = () => invite() !== undefined;
                            const userRole = currentUserRole();
                            const isNonOwner = props.embedded && userRole && userRole !== "Owner";
                            const isModal = props.embedded === true;
                            // Only apply 70% width for non-owners in modal when showing single box
                            const shouldBeNarrow = isModal && isNonOwner && availableRoles().length === 1;

                            return (
                              <button
                                onClick={() => {
                                  createLinkInvitationForRole(role);
                                }}
                                disabled={isCreatingInvite()}
                                class="group relative p-5 rounded-xl transition-all duration-200 text-left disabled:opacity-50 disabled:cursor-not-allowed bg-transparent border-2 border-dashed border-brand/30 hover:border-brand/50 w-full min-w-0"
                              >
                                <div class="flex flex-col items-start gap-3">
                                  <div class="w-full text-center">
                                    <h4 class="text-base font-semibold group-hover:text-brand transition-colors">
                                      {role}
                                    </h4>
                                    <p class="text-xs text-muted-text mt-1">
                                      {role === "Owner"
                                        ? "Full admin access"
                                        : role === "Collaborator"
                                          ? "Can chat and add Knowledge"
                                          : "Can read chats and Knowledge"}
                                    </p>
                                  </div>
                                  <div class="w-full">
                                    <StButton
                                      class="w-full bg-brand hover:bg-brand-hover text-on-brand text-sm py-2 rounded-lg transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        createLinkInvitationForRole(role);
                                      }}
                                      disabled={isCreatingInvite()}
                                    >
                                      <Show when={isCreatingInvite()} fallback="Create Secret Link">
                                        Creating...
                                      </Show>
                                    </StButton>
                                  </div>
                                </div>
                              </button>
                            );
                          }}
                        </For>
                      </div>
                    </div>

                    {/* Email and Domain invite cards */}
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 w-full min-w-0">
                      <button
                        onClick={() => setShowCreateEmailInvite(true)}
                        class="group relative p-5 bg-panel border-2 border-primary-border rounded-xl hover:border-primary-border/80 hover:shadow-lg transition-all duration-200 text-left w-full min-w-0"
                      >
                        <div class="flex items-start gap-4">
                          <div class="p-2.5 rounded-lg">
                            <TbMail class="w-5 h-5 text-muted-text" />
                          </div>
                          <div class="flex-1">
                            <h3 class="text-base font-semibold mb-1.5">Invite by Email</h3>
                            <p class="text-sm text-muted-text leading-relaxed">
                              Send personalized invitations to your colleagues
                            </p>
                            <StButton
                              class="mt-3 bg-brand hover:bg-brand-hover text-on-brand text-sm py-2 px-4 rounded-lg transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowCreateEmailInvite(true);
                              }}
                            >
                              <TbPlus class="w-4 h-4 mr-1 inline" /> Add Email
                            </StButton>
                          </div>
                        </div>
                      </button>

                      <button
                        onClick={() => setShowCreateDomainInvite(true)}
                        class="group relative p-5 bg-panel border-2 border-primary-border rounded-xl hover:border-primary-border/80 hover:shadow-lg transition-all duration-200 text-left w-full min-w-0"
                      >
                        <div class="flex items-start gap-4">
                          <div class="p-2.5 rounded-lg">
                            <TbWorld class="w-5 h-5 text-muted-text" />
                          </div>
                          <div class="flex-1">
                            <h3 class="text-base font-semibold mb-1.5">Domain Access</h3>
                            <p class="text-sm text-muted-text leading-relaxed">
                              Invite everyone from a domain (e.g., @company.com)
                            </p>
                            <StButton
                              class="mt-3 bg-brand hover:bg-brand-hover text-on-brand text-sm py-2 px-4 rounded-lg transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowCreateDomainInvite(true);
                              }}
                            >
                              <TbPlus class="w-4 h-4 mr-1 inline" /> Add Domain
                            </StButton>
                          </div>
                        </div>
                      </button>
                    </div>

                    {/* Show create forms */}
                    <div class="space-y-4">
                      <Show when={showCreateEmailInvite()}>
                        <CreateEmailInviteForm
                          newEmailInvites={newEmailInvites()}
                          pendingEmails={pendingEmails()}
                          currentEmailInput={currentEmailInput()}
                          selectedRole={newEmailRole()}
                          errorMessage={emailError()}
                          availableRoles={availableRoles()}
                          onPendingEmailsChange={setPendingEmails}
                          onCurrentInputChange={setCurrentEmailInput}
                          onRoleChange={setNewEmailRole}
                          onAddEmails={addNewEmailInvites}
                          onRemoveEmail={removeNewEmailInvite}
                          onUpdateEmailRole={updateNewEmailRole}
                          onSubmit={createEmailInvitations}
                          onClose={closeCreateEmailInvite}
                        />
                      </Show>

                      <Show when={showCreateDomainInvite()}>
                        <CreateDomainInviteForm
                          newDomainInvites={newDomainInvites()}
                          pendingDomains={pendingDomains()}
                          currentDomainInput={currentDomainInput()}
                          selectedRole={newDomainRole()}
                          errorMessage={domainError()}
                          availableRoles={availableRoles()}
                          onPendingDomainsChange={setPendingDomains}
                          onCurrentInputChange={setCurrentDomainInput}
                          onRoleChange={setNewDomainRole}
                          onAddDomains={addNewDomainInvites}
                          onRemoveDomain={removeNewDomainInvite}
                          onUpdateDomainRole={updateNewDomainRole}
                          onSubmit={createDomainInvitations}
                          onClose={closeCreateDomainInvite}
                        />
                      </Show>
                    </div>
                  </div>
                }
              >
                {/* Modal UI - Three boxes for secret links */}
                <div class="space-y-6">
                  {/* Secret Link Section */}
                  <div class="space-y-3">
                    <div class="flex items-center justify-between">
                      <h3 class="text-sm font-semibold flex items-center gap-2">
                        <TbLink class="w-4 h-4" />
                        Share a Secret Link
                      </h3>
                      <a
                        href="https://go.storytell.ai/learn-project-roles"
                        target="_blank"
                        rel="noreferrer"
                        class="inline-flex items-center gap-1 text-xs text-brand underline hover:underline-offset-2"
                      >
                        <TbHelpCircle class="w-3.5 h-3.5" />
                        Learn about Project Roles
                      </a>
                    </div>
                    <p class="text-xs text-muted-text">Anyone with this link will be able to join the project.</p>
                    <div
                      class={`w-full min-w-0 ${props.embedded && availableRoles().length === 1 ? "flex justify-center" : props.embedded ? "flex justify-center gap-4" : "grid grid-cols-1 md:grid-cols-3 gap-4"}`}
                    >
                      <For each={availableRoles()}>
                        {(role) => {
                          const invite = () => getInviteForRole(role);
                          const hasInvite = () => invite() !== undefined;
                          const userRole = currentUserRole();
                          const isNonOwner = props.embedded && userRole && userRole !== "Owner";
                          const isModal = props.embedded === true;
                          // Only apply 70% width for non-owners in modal when showing single box
                          const shouldBeNarrow = isModal && isNonOwner && availableRoles().length === 1;

                          return (
                            <button
                              onClick={() => {
                                if (hasInvite()) {
                                  copyInviteLink(invite()!);
                                } else {
                                  createLinkInvitationForRole(role);
                                }
                              }}
                              disabled={isCreatingInvite()}
                              class={`group relative p-5 rounded-xl transition-all duration-200 text-left disabled:opacity-50 disabled:cursor-not-allowed min-w-0 ${
                                shouldBeNarrow ? "w-[70%]" : props.embedded ? "w-[26%] min-w-[220px]" : "w-full"
                              } ${
                                hasInvite()
                                  ? "bg-gradient-to-br from-brand/5 to-brand/10 border-2 border-brand/20 hover:border-brand/40 hover:shadow-lg"
                                  : "bg-transparent border-2 border-dashed border-brand/30 hover:border-brand/50"
                              }`}
                            >
                              <div class="flex flex-col items-start gap-3">
                                <div class="w-full text-center">
                                  <h4 class="text-base font-semibold group-hover:text-brand transition-colors">
                                    {role}
                                  </h4>
                                  <p class="text-xs text-muted-text mt-1">
                                    {role === "Owner"
                                      ? "Full admin access"
                                      : role === "Collaborator"
                                        ? "Can chat and add Knowledge"
                                        : "Can read chats and Knowledge"}
                                  </p>
                                </div>
                                <Show
                                  when={hasInvite()}
                                  fallback={
                                    <div class="w-full">
                                      <StButton
                                        class="w-full bg-brand hover:bg-brand-hover text-on-brand text-sm py-2 rounded-lg transition-colors"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          createLinkInvitationForRole(role);
                                        }}
                                        disabled={isCreatingInvite()}
                                      >
                                        <Show when={isCreatingInvite()} fallback="Create Secret Link">
                                          Creating...
                                        </Show>
                                      </StButton>
                                    </div>
                                  }
                                >
                                  <div class="w-full">
                                    <StButton
                                      class="w-full bg-brand hover:bg-brand-hover text-on-brand text-sm py-2 rounded-lg transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        copyInviteLink(invite()!);
                                      }}
                                    >
                                      <Show
                                        when={copiedInviteId() === invite()?.id}
                                        fallback={
                                          <>
                                            <TbCopy class="w-4 h-4 mr-1 inline" /> Copy Link
                                          </>
                                        }
                                      >
                                        <>
                                          <TbCheck class="w-4 h-4 mr-1 inline" /> Copied!
                                        </>
                                      </Show>
                                    </StButton>
                                    <Show when={role === "Owner" && copiedOwnerInviteIds().has(invite()?.id || "")}>
                                      <div class="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                                        <p class="text-xs text-black dark:text-black flex items-start gap-2">
                                          <TbAlertTriangle class="w-4 h-4 flex-shrink-0 mt-0.5" />
                                          <span>
                                            <span class="font-semibold">Be careful:</span> Anyone with this link will
                                            become an owner with full admin rights.
                                          </span>
                                        </p>
                                      </div>
                                    </Show>
                                  </div>
                                </Show>
                              </div>
                            </button>
                          );
                        }}
                      </For>
                    </div>
                  </div>

                  {/* Email and Domain invite cards - Hide in modal, show on project management page */}
                  <Show when={!props.embedded}>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 w-full min-w-0">
                      <button
                        onClick={() => setShowCreateEmailInvite(true)}
                        class="group relative p-5 bg-panel border-2 border-primary-border rounded-xl hover:border-primary-border/80 hover:shadow-lg transition-all duration-200 text-left w-full min-w-0"
                      >
                        <div class="flex items-start gap-4">
                          <div class="p-2.5 rounded-lg">
                            <TbMail class="w-5 h-5 text-muted-text" />
                          </div>
                          <div class="flex-1">
                            <h3 class="text-base font-semibold mb-1.5">Invite by Email</h3>
                            <p class="text-sm text-muted-text leading-relaxed">
                              Send personalized invitations to your colleagues
                            </p>
                            <StButton
                              class="mt-3 bg-brand hover:bg-brand-hover text-on-brand text-sm py-2 px-4 rounded-lg transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowCreateEmailInvite(true);
                              }}
                            >
                              <TbPlus class="w-4 h-4 mr-1 inline" /> Add Email
                            </StButton>
                          </div>
                        </div>
                      </button>

                      <button
                        onClick={() => setShowCreateDomainInvite(true)}
                        class="group relative p-5 bg-panel border-2 border-primary-border rounded-xl hover:border-primary-border/80 hover:shadow-lg transition-all duration-200 text-left w-full min-w-0"
                      >
                        <div class="flex items-start gap-4">
                          <div class="p-2.5 rounded-lg">
                            <TbWorld class="w-5 h-5 text-muted-text" />
                          </div>
                          <div class="flex-1">
                            <h3 class="text-base font-semibold mb-1.5">Domain Access</h3>
                            <p class="text-sm text-muted-text leading-relaxed">
                              Invite everyone from a domain (e.g., @company.com)
                            </p>
                            <StButton
                              class="mt-3 bg-brand hover:bg-brand-hover text-on-brand text-sm py-2 px-4 rounded-lg transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowCreateDomainInvite(true);
                              }}
                            >
                              <TbPlus class="w-4 h-4 mr-1 inline" /> Add Domain
                            </StButton>
                          </div>
                        </div>
                      </button>
                    </div>

                    {/* Show create forms */}
                    <div class="space-y-4">
                      <Show when={showCreateEmailInvite()}>
                        <CreateEmailInviteForm
                          newEmailInvites={newEmailInvites()}
                          pendingEmails={pendingEmails()}
                          currentEmailInput={currentEmailInput()}
                          selectedRole={newEmailRole()}
                          errorMessage={emailError()}
                          availableRoles={availableRoles()}
                          onPendingEmailsChange={setPendingEmails}
                          onCurrentInputChange={setCurrentEmailInput}
                          onRoleChange={setNewEmailRole}
                          onAddEmails={addNewEmailInvites}
                          onRemoveEmail={removeNewEmailInvite}
                          onUpdateEmailRole={updateNewEmailRole}
                          onSubmit={createEmailInvitations}
                          onClose={closeCreateEmailInvite}
                        />
                      </Show>

                      <Show when={showCreateDomainInvite()}>
                        <CreateDomainInviteForm
                          newDomainInvites={newDomainInvites()}
                          pendingDomains={pendingDomains()}
                          currentDomainInput={currentDomainInput()}
                          selectedRole={newDomainRole()}
                          errorMessage={domainError()}
                          availableRoles={availableRoles()}
                          onPendingDomainsChange={setPendingDomains}
                          onCurrentInputChange={setCurrentDomainInput}
                          onRoleChange={setNewDomainRole}
                          onAddDomains={addNewDomainInvites}
                          onRemoveDomain={removeNewDomainInvite}
                          onUpdateDomainRole={updateNewDomainRole}
                          onSubmit={createDomainInvitations}
                          onClose={closeCreateDomainInvite}
                        />
                      </Show>
                    </div>
                  </Show>
                </div>
              </Show>
            }
          >
            <Show
              when={props.embedded}
              fallback={
                <div class="space-y-6">
                  {/* Secret Link Section - Project Management Page */}
                  <div class="space-y-3">
                    <div class="flex items-center justify-between">
                      <h3 class="text-sm font-semibold flex items-center gap-2">
                        <TbLink class="w-4 h-4" />
                        Share a Secret Link
                      </h3>
                      <a
                        href="https://go.storytell.ai/learn-project-roles"
                        target="_blank"
                        rel="noreferrer"
                        class="inline-flex items-center gap-1 text-xs text-brand underline hover:underline-offset-2"
                      >
                        <TbHelpCircle class="w-3.5 h-3.5" />
                        Learn about Project Roles
                      </a>
                    </div>
                    <p class="text-xs text-muted-text">Anyone with this link will be able to join the project.</p>
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 w-full min-w-0">
                      <For each={availableRoles()}>
                        {(role) => {
                          const invite = () => getInviteForRole(role);
                          const hasInvite = () => invite() !== undefined;
                          const canDelete = () => {
                            if (!hasInvite()) return false;
                            const inv = invite();
                            if (!inv) return false;
                            // Allow deletion if user has delete permissions OR if they created the invite
                            const hasDeletePermission = wire.grants.canDelete("cp.invitations");
                            const isCreator = (inv as any).createdBy === wire.auth.userId();
                            return hasDeletePermission || isCreator;
                          };

                          return (
                            <Show
                              when={hasInvite()}
                              fallback={
                                <button
                                  onClick={() => {
                                    createLinkInvitationForRole(role);
                                  }}
                                  disabled={isCreatingInvite()}
                                  class="group relative p-5 rounded-xl transition-all duration-200 text-left disabled:opacity-50 disabled:cursor-not-allowed bg-transparent border-2 border-dashed border-brand/30 hover:border-brand/50"
                                >
                                  <div class="flex flex-col items-start gap-3">
                                    <div class="w-full text-center">
                                      <h4 class="text-base font-semibold group-hover:text-brand transition-colors">
                                        {role}
                                      </h4>
                                      <p class="text-xs text-muted-text mt-1">
                                        {role === "Owner"
                                          ? "Full admin access"
                                          : role === "Collaborator"
                                            ? "Can chat and add Knowledge"
                                            : "Can read chats and Knowledge"}
                                      </p>
                                    </div>
                                    <div class="w-full">
                                      <StButton
                                        class="w-full bg-brand hover:bg-brand-hover text-on-brand text-sm py-2 rounded-lg transition-colors"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          createLinkInvitationForRole(role);
                                        }}
                                        disabled={isCreatingInvite()}
                                      >
                                        <Show when={isCreatingInvite()} fallback="Create Secret Link">
                                          Creating...
                                        </Show>
                                      </StButton>
                                    </div>
                                  </div>
                                </button>
                              }
                            >
                              <div class="group relative p-5 rounded-xl transition-all duration-200 bg-gradient-to-br from-brand/5 to-brand/10 border-2 border-brand/20 hover:border-brand/40 hover:shadow-lg w-full min-w-0">
                                <div class="flex flex-col items-start gap-3">
                                  <div class="w-full text-center">
                                    <h4 class="text-base font-semibold group-hover:text-brand transition-colors">
                                      {role}
                                    </h4>
                                    <p class="text-xs text-muted-text mt-1">
                                      {role === "Owner"
                                        ? "Full admin access"
                                        : role === "Collaborator"
                                          ? "Can chat and add Knowledge"
                                          : "Can read chats and Knowledge"}
                                    </p>
                                  </div>
                                  <div class="w-full flex items-center gap-2">
                                    <StButton
                                      class="flex-1 bg-brand hover:bg-brand-hover text-on-brand text-sm py-2 rounded-lg transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        copyInviteLink(invite()!);
                                      }}
                                    >
                                      <Show
                                        when={copiedInviteId() === invite()?.id}
                                        fallback={
                                          <>
                                            <TbCopy class="w-4 h-4 mr-1 inline" /> Copy Link
                                          </>
                                        }
                                      >
                                        <>
                                          <TbCheck class="w-4 h-4 mr-1 inline" /> Copied!
                                        </>
                                      </Show>
                                    </StButton>
                                    <StButton
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (canDelete()) {
                                          handleDeleteLinkInvite(invite()!.id, invite()!.role);
                                        }
                                      }}
                                      disabled={!canDelete()}
                                      class="shrink-0 text-red-500 hover:text-red-600 hover:bg-red-500/10 p-2 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                      title={
                                        canDelete() ? "Delete link" : "You don't have permission to delete this link"
                                      }
                                    >
                                      <TbTrash class="w-4 h-4" />
                                    </StButton>
                                  </div>
                                  <Show when={role === "Owner" && copiedOwnerInviteIds().has(invite()?.id || "")}>
                                    <div class="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                                      <p class="text-xs text-black dark:text-black flex items-start gap-2">
                                        <TbAlertTriangle class="w-4 h-4 flex-shrink-0 mt-0.5" />
                                        <span>
                                          <span class="font-semibold">Be careful:</span> Anyone with this link will
                                          become an owner with full admin rights.
                                        </span>
                                      </p>
                                    </div>
                                  </Show>
                                </div>
                              </div>
                            </Show>
                          );
                        }}
                      </For>
                    </div>
                  </div>

                  {/* Email Invites Section */}
                  <div id="email-invites-section" class="space-y-4">
                    <div class="flex items-center justify-between pb-2 border-b border-primary-border/50">
                      <div class="flex items-center gap-3">
                        <div class="p-2 bg-gray-500/10 rounded-lg">
                          <TbMail class="w-5 h-5 text-muted-text" />
                        </div>
                        <div>
                          <h3 class="text-sm font-semibold">Invite by Email</h3>
                          <p class="text-xs text-muted-text">Send personalized invitations to your colleagues</p>
                        </div>
                      </div>
                      <StButton
                        onClick={() => setShowCreateEmailInvite(true)}
                        class="bg-brand hover:bg-brand-hover text-on-brand text-sm py-2 px-4 rounded-lg transition-colors flex items-center gap-1"
                      >
                        <TbPlus class="w-4 h-4" />
                        Add Email
                      </StButton>
                    </div>

                    <Show when={emailInvitesData().length > 0}>
                      <div class="space-y-2">
                        <For each={emailInvitesData()}>
                          {(item) => (
                            <EmailInviteCard
                              email={item?.email ?? ""}
                              role={item?.role ?? ""}
                              inviteId={item?.inviteId ?? ""}
                              inviteLink={item?.inviteLink ?? ""}
                              isCopied={copiedInviteId() === item?.inviteId}
                              onCopy={copyLink}
                              onDelete={handleDeleteEmailFromInvite}
                            />
                          )}
                        </For>
                      </div>
                    </Show>

                    <Show when={showCreateEmailInvite()}>
                      <CreateEmailInviteForm
                        newEmailInvites={newEmailInvites()}
                        pendingEmails={pendingEmails()}
                        currentEmailInput={currentEmailInput()}
                        selectedRole={newEmailRole()}
                        errorMessage={emailError()}
                        onPendingEmailsChange={setPendingEmails}
                        onCurrentInputChange={setCurrentEmailInput}
                        onRoleChange={setNewEmailRole}
                        onAddEmails={addNewEmailInvites}
                        onRemoveEmail={removeNewEmailInvite}
                        onUpdateEmailRole={updateNewEmailRole}
                        onSubmit={createEmailInvitations}
                        onClose={closeCreateEmailInvite}
                      />
                    </Show>
                  </div>

                  {/* Domain Invites Section */}
                  <div id="domain-invites-section" class="space-y-4">
                    <div class="flex items-center justify-between pb-2 border-b border-primary-border/50">
                      <div class="flex items-center gap-3">
                        <div class="p-2 bg-gray-500/10 rounded-lg">
                          <TbWorld class="w-5 h-5 text-muted-text" />
                        </div>
                        <div>
                          <h3 class="text-sm font-semibold">Domain Access</h3>
                          <p class="text-xs text-muted-text">Invite everyone from a domain (e.g., @company.com)</p>
                        </div>
                      </div>
                      <StButton
                        onClick={() => setShowCreateDomainInvite(true)}
                        class="bg-brand hover:bg-brand-hover text-on-brand text-sm py-2 px-4 rounded-lg transition-colors flex items-center gap-1"
                      >
                        <TbPlus class="w-4 h-4" />
                        Add Domain
                      </StButton>
                    </div>

                    <Show when={domainInvitesData().length > 0}>
                      <div class="space-y-2">
                        <For each={domainInvitesData()}>
                          {(item) => (
                            <DomainInviteCard
                              domain={item?.domain ?? ""}
                              role={item?.role ?? ""}
                              inviteId={item?.inviteId ?? ""}
                              inviteLink={item?.inviteLink ?? ""}
                              isCopied={copiedInviteId() === item?.inviteId}
                              onCopy={copyLink}
                              onDelete={handleDeleteDomainFromInvite}
                            />
                          )}
                        </For>
                      </div>
                    </Show>

                    <Show when={showCreateDomainInvite()}>
                      <CreateDomainInviteForm
                        newDomainInvites={newDomainInvites()}
                        pendingDomains={pendingDomains()}
                        currentDomainInput={currentDomainInput()}
                        selectedRole={newDomainRole()}
                        errorMessage={domainError()}
                        availableRoles={availableRoles()}
                        onPendingDomainsChange={setPendingDomains}
                        onCurrentInputChange={setCurrentDomainInput}
                        onRoleChange={setNewDomainRole}
                        onAddDomains={addNewDomainInvites}
                        onRemoveDomain={removeNewDomainInvite}
                        onUpdateDomainRole={updateNewDomainRole}
                        onSubmit={createDomainInvitations}
                        onClose={closeCreateDomainInvite}
                      />
                    </Show>
                  </div>
                </div>
              }
            >
              {/* Modal UI - Three boxes for secret links when invitations exist */}
              <div class="space-y-6">
                {/* Secret Link Section */}
                <div class="space-y-3">
                  <div class="flex items-center justify-between">
                    <h3 class="text-sm font-semibold flex items-center gap-2">
                      <TbLink class="w-4 h-4" />
                      Share a Secret Link
                    </h3>
                    <a
                      href="https://go.storytell.ai/learn-project-roles"
                      target="_blank"
                      rel="noreferrer"
                      class="inline-flex items-center gap-1 text-xs text-brand underline hover:underline-offset-2"
                    >
                      <TbHelpCircle class="w-3.5 h-3.5" />
                      Learn about Project Roles
                    </a>
                  </div>
                  <p class="text-xs text-muted-text">Anyone with this link will be able to join the project.</p>
                  <div
                    class={`w-full min-w-0 ${props.embedded && availableRoles().length === 1 ? "flex justify-center" : props.embedded ? "flex justify-center gap-3" : "grid grid-cols-1 md:grid-cols-3 gap-4"}`}
                  >
                    <For each={availableRoles()}>
                      {(role) => {
                        const invite = () => getInviteForRole(role);
                        const hasInvite = () => invite() !== undefined;
                        const userRole = currentUserRole();
                        const isNonOwner = props.embedded && userRole && userRole !== "Owner";
                        const isModal = props.embedded === true;
                        // Only apply 70% width for non-owners in modal when showing single box
                        const shouldBeNarrow = isModal && isNonOwner && availableRoles().length === 1;

                        return (
                          <Show
                            when={hasInvite()}
                            fallback={
                              <button
                                onClick={() => {
                                  createLinkInvitationForRole(role);
                                }}
                                disabled={isCreatingInvite()}
                                class={`group relative p-5 rounded-xl transition-all duration-200 text-left disabled:opacity-50 disabled:cursor-not-allowed bg-transparent border-2 border-dashed border-brand/30 hover:border-brand/50 min-w-0 ${
                                  shouldBeNarrow ? "w-[70%]" : props.embedded ? "w-[26%] min-w-[220px]" : "w-full"
                                }`}
                              >
                                <div class="flex flex-col items-start gap-3">
                                  <div class="w-full text-center">
                                    <h4 class="text-base font-semibold group-hover:text-brand transition-colors">
                                      {role}
                                    </h4>
                                    <p class="text-xs text-muted-text mt-1">
                                      {role === "Owner"
                                        ? "Full admin access"
                                        : role === "Collaborator"
                                          ? "Can chat and add Knowledge"
                                          : "Can read chats and Knowledge"}
                                    </p>
                                  </div>
                                  <div class="w-full">
                                    <Show when={wire.grants.canDelete("cp.invitations")}>
                                      <StButton
                                        class="w-full bg-brand hover:bg-brand-hover text-on-brand text-sm py-2 rounded-lg transition-colors"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          createLinkInvitationForRole(role);
                                        }}
                                        disabled={isCreatingInvite()}
                                      >
                                        <Show when={isCreatingInvite()} fallback="Create Secret Link">
                                          Creating...
                                        </Show>
                                      </StButton>
                                    </Show>
                                  </div>
                                </div>
                              </button>
                            }
                          >
                            <div
                              class={`group relative p-5 rounded-xl transition-all duration-200 bg-gradient-to-br from-brand/5 to-brand/10 border-2 border-brand/20 hover:border-brand/40 hover:shadow-lg min-w-0 ${
                                shouldBeNarrow ? "w-[70%]" : props.embedded ? "w-[26%] min-w-[220px]" : "w-full"
                              }`}
                            >
                              <div class="flex flex-col items-start gap-3">
                                <div class="w-full text-center">
                                  <h4 class="text-base font-semibold group-hover:text-brand transition-colors">
                                    {role}
                                  </h4>
                                  <p class="text-xs text-muted-text mt-1">
                                    {role === "Owner"
                                      ? "Full admin access"
                                      : role === "Collaborator"
                                        ? "Can chat and add Knowledge"
                                        : "Can read chats and Knowledge"}
                                  </p>
                                </div>
                                <div class="w-full">
                                  <StButton
                                    class="w-full bg-brand hover:bg-brand-hover text-on-brand text-sm py-2 rounded-lg transition-colors"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      copyInviteLink(invite()!);
                                    }}
                                  >
                                    <Show
                                      when={copiedInviteId() === invite()?.id}
                                      fallback={
                                        <>
                                          <TbCopy class="w-4 h-4 mr-1 inline" /> Copy Link
                                        </>
                                      }
                                    >
                                      <>
                                        <TbCheck class="w-4 h-4 mr-1 inline" /> Copied!
                                      </>
                                    </Show>
                                  </StButton>
                                  <Show when={role === "Owner" && copiedOwnerInviteIds().has(invite()?.id || "")}>
                                    <div class="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                                      <p class="text-xs text-black dark:text-black flex items-start gap-2">
                                        <TbAlertTriangle class="w-4 h-4 flex-shrink-0 mt-0.5" />
                                        <span>
                                          <span class="font-semibold">Be careful:</span> Anyone with this link will
                                          become an owner with full admin rights.
                                        </span>
                                      </p>
                                    </div>
                                  </Show>
                                </div>
                              </div>
                            </div>
                          </Show>
                        );
                      }}
                    </For>
                  </div>
                </div>

                {/* Email Invites Section - Hide in modal, show on project management page */}
                <Show when={!props.embedded}>
                  <div class="space-y-4">
                    <div class="flex items-center justify-between pb-2 border-b border-primary-border/50">
                      <div class="flex items-center gap-3">
                        <div class="p-2 bg-gray-500/10 rounded-lg">
                          <TbMail class="w-5 h-5 text-muted-text" />
                        </div>
                        <div>
                          <h3 class="text-sm font-semibold">Invite by Email</h3>
                          <p class="text-xs text-muted-text">Send personalized invitations to your colleagues</p>
                        </div>
                      </div>
                      <StButton
                        onClick={() => setShowCreateEmailInvite(true)}
                        class="bg-brand hover:bg-brand-hover text-on-brand text-sm py-2 px-4 rounded-lg transition-colors flex items-center gap-1"
                      >
                        <TbPlus class="w-4 h-4" />
                        Add Email
                      </StButton>
                    </div>

                    {/* Only show existing email invites on project management page, not in modal */}
                    <Show when={!props.embedded && emailInvitesData().length > 0}>
                      <div class="space-y-2">
                        <For each={emailInvitesData()}>
                          {(item) => (
                            <EmailInviteCard
                              email={item?.email ?? ""}
                              role={item?.role ?? ""}
                              inviteId={item?.inviteId ?? ""}
                              inviteLink={item?.inviteLink ?? ""}
                              isCopied={copiedInviteId() === item?.inviteId}
                              onCopy={copyLink}
                              onDelete={handleDeleteEmailFromInvite}
                            />
                          )}
                        </For>
                      </div>
                    </Show>

                    <Show when={showCreateEmailInvite()}>
                      <CreateEmailInviteForm
                        newEmailInvites={newEmailInvites()}
                        pendingEmails={pendingEmails()}
                        currentEmailInput={currentEmailInput()}
                        selectedRole={newEmailRole()}
                        errorMessage={emailError()}
                        availableRoles={availableRoles()}
                        onPendingEmailsChange={setPendingEmails}
                        onCurrentInputChange={setCurrentEmailInput}
                        onRoleChange={setNewEmailRole}
                        onAddEmails={addNewEmailInvites}
                        onRemoveEmail={removeNewEmailInvite}
                        onUpdateEmailRole={updateNewEmailRole}
                        onSubmit={createEmailInvitations}
                        onClose={closeCreateEmailInvite}
                      />
                    </Show>
                  </div>
                </Show>

                {/* Domain Invites Section - Hide in modal, show on project management page */}
                <Show when={!props.embedded}>
                  <div class="space-y-4">
                    <div class="flex items-center justify-between pb-2 border-b border-primary-border/50">
                      <div class="flex items-center gap-3">
                        <div class="p-2 bg-gray-500/10 rounded-lg">
                          <TbWorld class="w-5 h-5 text-muted-text" />
                        </div>
                        <div>
                          <h3 class="text-sm font-semibold">Domain Access</h3>
                          <p class="text-xs text-muted-text">Invite everyone from a domain (e.g., @company.com)</p>
                        </div>
                      </div>
                      <StButton
                        onClick={() => setShowCreateDomainInvite(true)}
                        class="bg-brand hover:bg-brand-hover text-on-brand text-sm py-2 px-4 rounded-lg transition-colors flex items-center gap-1"
                      >
                        <TbPlus class="w-4 h-4" />
                        Add Domain
                      </StButton>
                    </div>

                    {/* Only show existing domain invites on project management page, not in modal */}
                    <Show when={!props.embedded && domainInvitesData().length > 0}>
                      <div class="space-y-2">
                        <For each={domainInvitesData()}>
                          {(item) => (
                            <DomainInviteCard
                              domain={item?.domain ?? ""}
                              role={item?.role ?? ""}
                              inviteId={item?.inviteId ?? ""}
                              inviteLink={item?.inviteLink ?? ""}
                              isCopied={copiedInviteId() === item?.inviteId}
                              onCopy={copyLink}
                              onDelete={handleDeleteDomainFromInvite}
                            />
                          )}
                        </For>
                      </div>
                    </Show>

                    <Show when={showCreateDomainInvite()}>
                      <CreateDomainInviteForm
                        newDomainInvites={newDomainInvites()}
                        pendingDomains={pendingDomains()}
                        currentDomainInput={currentDomainInput()}
                        selectedRole={newDomainRole()}
                        errorMessage={domainError()}
                        availableRoles={availableRoles()}
                        onPendingDomainsChange={setPendingDomains}
                        onCurrentInputChange={setCurrentDomainInput}
                        onRoleChange={setNewDomainRole}
                        onAddDomains={addNewDomainInvites}
                        onRemoveDomain={removeNewDomainInvite}
                        onUpdateDomainRole={updateNewDomainRole}
                        onSubmit={createDomainInvitations}
                        onClose={closeCreateDomainInvite}
                      />
                    </Show>
                  </div>
                </Show>

                {/* Owner link to project management page - at bottom of modal */}
                <Show when={props.embedded && currentUserRole() === "Owner"}>
                  <div class="pt-4 border-t border-primary-border mt-6">
                    <p class="text-sm text-muted-text text-center">
                      Visit the{" "}
                      <a
                        href={`/projects/${props.projectId}#share`}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`/projects/${props.projectId}#share`);
                          // Scroll to share section after navigation
                          setTimeout(() => {
                            const shareSection = document.getElementById("share-section");
                            if (shareSection) {
                              shareSection.scrollIntoView({ behavior: "smooth", block: "start" });
                            }
                          }, 300);
                        }}
                        class="text-brand hover:text-brand-hover underline font-medium"
                      >
                        project management page
                      </a>{" "}
                      to invite users via <span class="font-semibold">email</span> or{" "}
                      <span class="font-semibold">domain access</span>.
                    </p>
                  </div>
                </Show>
              </div>
            </Show>
          </Show>
        </Suspense>
      </div>

      {/* Delete Modals */}
      <DeleteInviteModal
        isOpen={deleteModalOpen()}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeleteTarget(null);
        }}
        onConfirm={confirmDeleteLinkInvite}
        inviteType={deleteTarget() ? mapRoleToUI(deleteTarget()!.role) : ""}
      />

      <DeleteDomainModal
        isOpen={domainDeleteModalOpen()}
        onClose={() => {
          setDomainDeleteModalOpen(false);
          setDomainDeleteTarget(null);
        }}
        onConfirm={confirmDeleteDomainFromInvite}
        domain={domainDeleteTarget()?.domain || ""}
        inviteType={domainDeleteTarget() ? mapRoleToUI(domainDeleteTarget()!.role) : ""}
      />

      <DeleteEmailModal
        isOpen={emailDeleteModalOpen()}
        onClose={() => {
          setEmailDeleteModalOpen(false);
          setEmailDeleteTarget(null);
        }}
        onConfirm={confirmDeleteEmailFromInvite}
        email={emailDeleteTarget()?.email || ""}
        inviteType={mapRoleToUI(emailDeleteTarget()?.role || "")}
      />
    </>
  );
};
